import { createApi } from "@reduxjs/toolkit/query/react";
import { APP_CONFIG } from "../../constants/constants";
import { localStorageKeys } from "../../utils/localStorageItems";
import {
  CameraListingPayload,
  CameraListingPayloadPostBody,
  CameraListingResponse,
  CameraStatusCode,
  CameraStatusCodesListingPayload,
  DeviceEvent,
  DeviceEventDetailResponse,
  DeviceEventsListPayload,
  DeviceListingResponse,
  FileRetrievalListingPayload,
  FileRetrievalListingResponse,
  RequestVideoPayload,
  TripSummary,
} from "../types/Camera";
import { VMResponse } from "../types/CamerasAdminVM";
import { ListingResponse } from "../types/Listing";
import {
  appBaseQuery,
  baseQueryWithAuthHandling,
  preparedAuthHeader,
} from "../utils/utils";
import {
  getAccountConfiguredAssetSearchFields,
  getSearchFieldsParams,
} from "./adminApiServices";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS, API_SERVER_URL } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";
import { CamerasAdminVMApiTags } from "./vmApiService";

export const exportCSV = async (
  payload: DeviceEventsListPayload
): Promise<Blob> => {
  const { serial_number, start_time, end_time, ...restParams } = payload;
  const baseUrl = API_ENDPOINTS.CAMERA.EVENTS(
    serial_number,
    start_time || 0,
    end_time || 0
  );
  const query = new URLSearchParams(
    Object.entries(restParams).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = String(value);
        }
        return acc;
      },
      {} as Record<string, string>
    )
  ).toString();

  const finalUrl = `${API_SERVER_URL}${baseUrl}&${query}`;

  try {
    const response = await fetch(finalUrl, {
      method: API_METHODS.GET,
      headers: {
        // Adjust Content-Type only if needed; many CSV endpoints return plain text
        "Content-Type": "application/json",
        Authorization: preparedAuthHeader(
          localStorage.getItem(localStorageKeys.accessToken)
        ),
      },
    });

    if (!response.ok) {
      const errorData = await response.json(); // Still handle JSON errors if they return it
      handleAuthErrorCode(errorData);
      throw new Error("API call failed");
    }

    const blob = await response.blob(); //
    return blob;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export const reducerPath = "cameraVMApi";

const assetSearchFieldConversionMap = {
  deviceid: "ft_device_id",
  description: "ft_device_description",
  displayname: "ft_displayname",
};

export const CamerasAdminVMAPIs = createApi({
  reducerPath,
  tagTypes: [
    CamerasAdminVMApiTags.SD_CARD_FILE_DELETE,
    CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    // get all device listing
    getDeviceListing: builder.query<
      CameraListingResponse<DeviceListingResponse[]>,
      CameraListingPayload
    >({
      query: ({
        camera_ids,
        driver_id,
        group_ids,
        linked_to_driver,
        ignore_not_initiated,
        search_fields,
        ...restParams
      }: CameraListingPayload) => {
        const body: CameraListingPayloadPostBody = {
          camera_ids: camera_ids,
          driver_id: driver_id,
          group_ids: group_ids,
          linked_to_driver: !!driver_id ? linked_to_driver : false,
          ignore_not_initiated: ignore_not_initiated ?? false,
        };
        return {
          url: API_ENDPOINTS.CAMERA.DEVICE_LISTING,
          params: {
            ...restParams,
            ...getSearchFieldsParams(search_fields, [
              ...getAccountConfiguredAssetSearchFields()
                .map(
                  (field) =>
                    assetSearchFieldConversionMap[
                      field as keyof typeof assetSearchFieldConversionMap
                    ] ?? field
                )
                // filter out 'vehicleid' in this specific case as that is a backend requirement to exclude here
                .filter((field) => field !== "vehicleid"),
              ...APP_CONFIG.ENTITY_SEARCH_FIELDS.CAMERA_LISTING,
            ]),
          },
          method: API_METHODS.POST,
          body,
        };
      },
      providesTags: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: CameraListingResponse<DeviceListingResponse[]>
      ) => {
        return response;
      },
    }),

    // get specific device events list
    getDeviceEventsList: builder.query<
      ListingResponse<DeviceEvent[]>,
      DeviceEventsListPayload
    >({
      query: ({
        serial_number,
        start_time,
        end_time,
        ...restParams
      }: DeviceEventsListPayload) => {
        return {
          url: API_ENDPOINTS.CAMERA.EVENTS(
            serial_number,
            start_time as number,
            end_time as number
          ),
          method: API_METHODS.GET,
          params: restParams,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<DeviceEvent[]>) => {
        return response;
      },
    }),

    // get specific device event details
    getDeviceEventDetails: builder.query<DeviceEventDetailResponse, string>({
      query: (recordId: string) => {
        return {
          url: API_ENDPOINTS.CAMERA.EVENT_DETAILS(recordId),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: DeviceEventDetailResponse) => {
        return response;
      },
    }),

    getCamTripSummary: builder.query<TripSummary, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.CAMERA.TRIP_SUMMARY,
        };
      },
      transformResponse: (response: TripSummary) => {
        return response;
      },
    }),

    // get all file retrieval listing
    getFileRetrievalListing: builder.query<
      CameraListingResponse<FileRetrievalListingResponse[]>,
      FileRetrievalListingPayload
    >({
      query: (params: FileRetrievalListingPayload) => {
        return {
          url: API_ENDPOINTS.CAMERA.FILE_RETRIEVAL_LISTING,
          params: params,
          method: API_METHODS.GET,
        };
      },
      providesTags: [CamerasAdminVMApiTags.SD_CARD_FILE_DELETE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: CameraListingResponse<FileRetrievalListingResponse[]>
      ) => {
        return response;
      },
    }),

    // request video
    requestVideo: builder.mutation<
      VMResponse<{ task_id: string }>,
      RequestVideoPayload
    >({
      query: (body: RequestVideoPayload) => {
        return {
          url: API_ENDPOINTS.CAMERA.REQUEST_VIDEO,
          method: API_METHODS.POST,
          body: { ...body, status: Math.random() },
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: VMResponse<{ task_id: string }>) => {
        return response;
      },
    }),
    // get all camera status codes
    // the API returns complete list of codes in a single call
    cameraStatusCodes: builder.query<
      CameraStatusCode[],
      CameraStatusCodesListingPayload
    >({
      query: (params: CameraStatusCodesListingPayload) => {
        return {
          url: API_ENDPOINTS.CAMERA.STATUS_CODES,
          params: params,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: CameraStatusCode[]) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetDeviceListingQuery,
  useLazyGetDeviceListingQuery,
  useGetDeviceEventsListQuery,
  useGetDeviceEventDetailsQuery,
  useGetCamTripSummaryQuery,
  useGetFileRetrievalListingQuery,
  useRequestVideoMutation,
  useCameraStatusCodesQuery,
  useLazyCameraStatusCodesQuery,
} = CamerasAdminVMAPIs;
